"use client";

import Link from "next/link";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import {
  IconaCarrello,
  IconaImpostazioni,
  IconaMenu,
  IconaPersona,
} from "@/app/components/Icone";
import { Logo } from "@/app/components/Logo";
import { listProducts, nomePaese, type Facets } from "@/lib/api";

/**
 * La cornice: barra in alto, menù a tendina a sinistra, impostazioni a destra.
 *
 * Sta qui e non nelle singole pagine perché la barra è la stessa ovunque, ma
 * soprattutto perché **lo stato del menù cambia l'impaginazione del contenuto**:
 * a tendina aperta i risultati diventano una striscia orizzontale sopra la chat,
 * a tendina chiusa una griglia verticale accanto. Il contenuto deve poterlo
 * sapere, e lo sa da questo contesto invece che indovinandolo.
 *
 * `logoNellaPagina` risolve il caso opposto: nello stato vuoto il marchio sta
 * grande in mezzo alla pagina, e averlo anche nella barra sarebbe lo stesso
 * segno due volte. La pagina lo dichiara, la barra si toglie di mezzo.
 */

type Cornice = {
  menuAperto: boolean;
  setMenuAperto: (aperto: boolean) => void;
  logoNellaPagina: boolean;
  setLogoNellaPagina: (dentro: boolean) => void;
};

const Contesto = createContext<Cornice | null>(null);

export function useCornice(): Cornice {
  const c = useContext(Contesto);
  if (!c) throw new Error("useCornice va usato dentro <Chrome>");
  return c;
}

/* ── Tema ─────────────────────────────────────────────────────────────────── */

type Tema = "chiaro" | "scuro" | "sistema";

const TEMI: { valore: Tema; etichetta: string }[] = [
  { valore: "sistema", etichetta: "Come il sistema" },
  { valore: "chiaro", etichetta: "Chiaro" },
  { valore: "scuro", etichetta: "Scuro" },
];

/* Il tema vive **fuori da React**: sta in `localStorage`, ci arriva anche da
   un'altra scheda, e viene applicato prima del primo disegno da uno script nel
   documento. Leggerlo con uno stato piu' un effetto significherebbe render a
   cascata e un lampo di tema sbagliato; `useSyncExternalStore` e' lo strumento
   fatto per questo. */
const CHIAVE_TEMA = "youppybuy:tema";
const ascoltatori = new Set<() => void>();

function leggiTema(): Tema {
  try {
    return (window.localStorage.getItem(CHIAVE_TEMA) as Tema | null) ?? "sistema";
  } catch {
    // Modalita' privata o archiviazione negata: si resta sul tema di sistema.
    return "sistema";
  }
}

function sottoscriviTema(avvisa: () => void) {
  ascoltatori.add(avvisa);
  window.addEventListener("storage", avvisa);
  return () => {
    ascoltatori.delete(avvisa);
    window.removeEventListener("storage", avvisa);
  };
}

function applicaTema(t: Tema) {
  const html = document.documentElement;
  if (t === "sistema") html.removeAttribute("data-theme");
  else html.setAttribute("data-theme", t === "scuro" ? "dark" : "light");
  try {
    window.localStorage.setItem(CHIAVE_TEMA, t);
  } catch {
    /* niente da salvare: il tema vale per questa sessione */
  }
  ascoltatori.forEach((avvisa) => avvisa());
}

/* ── Pulsanti della barra ─────────────────────────────────────────────────── */

/** Un'icona sola, con l'etichetta per chi legge con la voce. Nella barra lo
 *  spazio è poco e le parole diventano cinque scritte in fila: l'icona si
 *  riconosce prima, purché l'etichetta ci sia per chi non la vede. */
function PulsanteIcona({
  etichetta,
  onClick,
  href,
  espanso,
  children,
}: {
  etichetta: string;
  onClick?: () => void;
  href?: string;
  espanso?: boolean;
  children: ReactNode;
}) {
  const classi =
    "flex items-center justify-center rounded-lg border border-line p-2 transition hover:border-accent";
  if (href) {
    return (
      <Link href={href} aria-label={etichetta} title={etichetta} className={classi}>
        {children}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={etichetta}
      title={etichetta}
      aria-expanded={espanso}
      aria-haspopup={espanso === undefined ? undefined : "menu"}
      className={classi}
    >
      {children}
    </button>
  );
}

/** Il riquadro che si apre sotto un pulsante della barra. */
function Tendinetta({
  chiudi,
  children,
}: {
  chiudi: () => void;
  children: ReactNode;
}) {
  return (
    <>
      {/* Uno strato invisibile che chiude al clic fuori: senza, il riquadro
          resta aperto e copre quello che si sta cercando di guardare. */}
      <div className="fixed inset-0 z-10" onClick={chiudi} aria-hidden />
      <div
        role="menu"
        className="absolute right-0 z-20 mt-2 w-64 rounded-xl border border-line bg-overlay p-3 shadow-lg"
      >
        {children}
      </div>
    </>
  );
}

/* La lingua la sceglie il mappamondo di YouppyApps, che vale per tutte le app (anche Youppybuy). */

/* ── Impostazioni ─────────────────────────────────────────────────────────── */

function Impostazioni() {
  const [aperto, setAperto] = useState(false);
  // Sul server non c'è `localStorage`: la terza funzione dice cosa rispondere
  // lì, e coincide con quello che lo script nel documento lascia se non trova
  // nulla salvato.
  const tema = useSyncExternalStore(sottoscriviTema, leggiTema, () => "sistema" as Tema);

  return (
    <div className="relative">
      <PulsanteIcona
        etichetta="Impostazioni"
        onClick={() => setAperto((a) => !a)}
        espanso={aperto}
      >
        <IconaImpostazioni className="size-5" />
      </PulsanteIcona>

      {aperto && (
        <Tendinetta chiudi={() => setAperto(false)}>
          <p className="text-xs font-medium text-muted">Aspetto</p>
          <div className="mt-1.5 space-y-1">
            {TEMI.map(({ valore, etichetta }) => (
              <button
                key={valore}
                type="button"
                role="menuitemradio"
                aria-checked={tema === valore}
                onClick={() => applicaTema(valore)}
                className={`block w-full rounded-lg px-2 py-1.5 text-left text-sm transition ${
                  tema === valore ? "bg-accent-soft font-medium" : "hover:bg-accent-soft"
                }`}
              >
                {etichetta}
              </button>
            ))}
          </div>

          <div className="mt-4 flex flex-col gap-1 border-t border-line pt-3 text-sm">
            <Link href="/azienda" className="text-accent underline underline-offset-2">
              La nostra azienda
            </Link>
            <Link href="/contatti" className="text-accent underline underline-offset-2">
              Contatti
            </Link>
            <Link href="/come-funziona" className="text-accent underline underline-offset-2">
              Come funziona
            </Link>
          </div>
        </Tendinetta>
      )}
    </div>
  );
}

/* ── Menù a tendina ───────────────────────────────────────────────────────── */

function Tendina({ aperto, chiudi }: { aperto: boolean; chiudi: () => void }) {
  const [facce, setFacce] = useState<Facets | null>(null);
  const [filtro, setFiltro] = useState("");
  // Se l'API non risponde lo si dice, invece di tre "nessuna voce" che fanno
  // sembrare vuoto un catalogo che c'e'.
  const [indiceRotto, setIndiceRotto] = useState(false);

  // L'indice si carica alla prima apertura, non al caricamento della pagina:
  // chi non apre il menù non paga la richiesta.
  useEffect(() => {
    if (!aperto || facce) return;
    listProducts({ limit: 1 })
      .then((r) => setFacce(r.facets))
      .catch(() => {
        setIndiceRotto(true);
        setFacce({ paesi: [], materiali: [], lavorazioni: [] });
      });
  }, [aperto, facce]);

  if (!aperto) return null;

  const cerca = (elenco: string[] | null) =>
    (elenco ?? []).filter((v) => v.toLowerCase().includes(filtro.trim().toLowerCase()));

  const sezioni: [string, string[], (v: string) => string][] = [
    ["Paesi", cerca(facce?.paesi ?? null), (v) => `/prodotti?paese=${v}`],
    ["Materiali", cerca(facce?.materiali ?? null), (v) => `/prodotti?materiale=${encodeURIComponent(v)}`],
    ["Lavorazioni", cerca(facce?.lavorazioni ?? null), () => "/prodotti"],
  ];

  return (
    <aside
      className="w-full shrink-0 border-line sm:w-64 sm:border-r"
      aria-label="Indice del catalogo"
    >
      <div className="sticky top-4 max-h-[calc(100vh-6rem)] overflow-y-auto p-4">
        {/* Prima chi siamo e come ci si parla, poi il catalogo: chi apre il
            menù su un sito che non conosce cerca prima quello. */}
        <nav className="mb-5 space-y-0.5 text-sm">
          {[
            ["/azienda", "La nostra azienda"],
            ["/contatti", "Contatti"],
            ["/come-funziona", "Come funziona"],
            ["/prodotti", "Catalogo"],
          ].map(([href, testo]) => (
            <Link
              key={href}
              href={href}
              onClick={chiudi}
              className="block rounded px-1.5 py-1.5 font-medium hover:bg-accent-soft"
            >
              {testo}
            </Link>
          ))}
        </nav>

        <label className="block">
          <span className="sr-only">Cerca nell&apos;indice</span>
          <input
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            placeholder="Cerca nell'indice"
            className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm"
          />
        </label>

        {facce === null ? (
          <p className="mt-4 text-sm text-muted">Carico l&apos;indice…</p>
        ) : indiceRotto ? (
          <p className="mt-4 text-sm text-muted">
            L&apos;indice non è disponibile in questo momento. Riprova fra poco.
          </p>
        ) : (
          sezioni.map(([titolo, voci, href]) => (
            <section key={titolo} className="mt-5">
              <h2 className="text-xs font-medium uppercase tracking-wide text-muted">
                {titolo}
              </h2>
              {voci.length === 0 ? (
                <p className="mt-1 text-sm text-muted">nessuna voce</p>
              ) : (
                <ul className="mt-1.5 space-y-0.5 text-sm">
                  {voci.map((voce) => (
                    <li key={voce}>
                      <Link
                        href={href(voce)}
                        onClick={chiudi}
                        className="block rounded px-1.5 py-1 hover:bg-accent-soft"
                      >
                        {titolo === "Paesi" ? nomePaese(voce) : voce}
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))
        )}
      </div>
    </aside>
  );
}

/* ── La cornice ───────────────────────────────────────────────────────────── */

export function Chrome({ children }: { children: ReactNode }) {
  const [menuAperto, setMenuAperto] = useState(false);
  const [logoNellaPagina, setLogoNellaPagina] = useState(false);

  const chiudi = useCallback(() => setMenuAperto(false), []);

  return (
    <Contesto.Provider
      value={{ menuAperto, setMenuAperto, logoNellaPagina, setLogoNellaPagina }}
    >
      {/* Senza riga di separazione: la barra e il contenuto sono la stessa
          superficie, e un filo orizzontale la spezzerebbe in due. */}
      <header>
        {/* Le due colonne laterali sono `1fr` uguali, non `auto`: con `auto` la
            colonna di destra pesa quattro icone e quella di sinistra una, e il
            marchio finiva al centro dello spazio che avanzava invece che al
            centro della barra. Si vede scorrendo, quando resta l'unica cosa
            ferma in alto. */}
        <div className="mx-auto grid max-w-7xl grid-cols-[1fr_auto_1fr] items-center gap-3 px-4 py-3 sm:px-6">
          <div className="justify-self-start">
            <PulsanteIcona
              etichetta={menuAperto ? "Chiudi il menù" : "Apri il menù"}
              onClick={() => setMenuAperto(!menuAperto)}
              espanso={menuAperto}
            >
              <IconaMenu className="size-5" />
            </PulsanteIcona>
          </div>

          <div className="justify-self-center">
            {!logoNellaPagina && (
              <Link href="/" aria-label="Youppybuy, vai alla ricerca">
                <Logo animato className="h-11 w-auto text-foreground sm:h-14" />
              </Link>
            )}
          </div>

          <div className="flex items-center gap-2 justify-self-end">
            <Impostazioni />
            <PulsanteIcona etichetta="Il tuo carrello" href="/carrello">
              <IconaCarrello className="size-5" />
            </PulsanteIcona>
            <PulsanteIcona etichetta="Accesso" href="/accesso">
              <IconaPersona className="size-5" />
            </PulsanteIcona>
          </div>
        </div>
      </header>

      <div className="mx-auto flex w-full max-w-7xl flex-col sm:flex-row">
        <Tendina aperto={menuAperto} chiudi={chiudi} />
        <main className="min-w-0 flex-1">{children}</main>
      </div>
    </Contesto.Provider>
  );
}
